import { addDaysToInputValue, isPastInputValue, toDateInputValue } from '@/shared/utils/date-input';

/** Fallback flight length when we have no usable end date to work with. */
export const DEFAULT_FLIGHT_DAYS = 14;

interface ScheduleSource {
  startDate?: string;
  endDate?: string;
}

/**
 * Turn a schedule proposed by something outside the form — an Events.com
 * catalog entry, an AI budget recommendation — into a window the organizer can
 * actually launch.
 *
 * The events endpoint returns every event the org has ever published with no
 * date filter, so picking one that already happened would otherwise drop a
 * past start date straight into the form, which every ad platform rejects.
 *
 * - Start is floored at today; a start that already passed becomes today.
 * - The proposed end is kept when it still lands after the (possibly floored)
 *   start — an event mid-run keeps its real finish date.
 * - Otherwise we fall back to a two-week flight so the form is never left with
 *   a window that ends before it begins.
 */
export function resolveLaunchableSchedule(
  source: ScheduleSource,
  today: string,
): { startDate: string; endDate: string } {
  const proposedStart = toDateInputValue(source.startDate);
  const proposedEnd = toDateInputValue(source.endDate);

  const startDate = proposedStart && proposedStart > today ? proposedStart : today;
  const endDate =
    proposedEnd && proposedEnd > startDate
      ? proposedEnd
      : addDaysToInputValue(startDate, DEFAULT_FLIGHT_DAYS);

  return { startDate, endDate };
}

/**
 * Repair the schedule of a draft restored from sessionStorage.
 *
 * The wizard store outlives the day it was written — a draft started last week
 * rehydrates with dates that have since elapsed — and "Save as Draft" posts the
 * stored values without going through form validation, so an unlaunchable window
 * can reach the API without ever failing a form rule. Returns null when the
 * stored schedule is still fine (or empty), so the caller can skip the write.
 */
export function repairPersistedSchedule(
  stored: ScheduleSource,
  today: string,
): { startDate: string; endDate: string } | null {
  if (!stored.startDate) return null;

  const startElapsed = isPastInputValue(stored.startDate);
  // An end that is missing or no longer follows the start is just as unlaunchable
  // as an elapsed start, and the draft-save path won't catch either.
  const endUnusable = !stored.endDate || stored.endDate <= stored.startDate;
  if (!startElapsed && !endUnusable) return null;

  return resolveLaunchableSchedule(stored, today);
}
